// Main story + question screen — wired to FastAPI via api/client.ts

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Exercise, PagePayload, StudentProfile, SubmitResponse } from '../api/types';
import { ApiError, startSession, submitAnswer, getNextPage, logHintRequested, reportClientEvent, getCurrentCheck } from '../api/client';
import type { WissensCheck } from '../api/client';
import SceneIllustration from '../components/scene/SceneIllustration';
import AdventurerIllustration from '../components/adventurer/AdventurerIllustration';
import type { AdventurerId } from '../components/adventurer/AdventurerStamp';
import { ADVENTURERS } from '../components/adventurer/AdventurerStamp';
import DialogueBubble from '../components/dialogue/DialogueBubble';
import { useCalloutTimeline } from '../components/dialogue/useCalloutTimeline';
import { HINT_WAIT_TIME_MS } from '../components/dialogue/config';
import { arcTitle } from '../lib/arcs';
import LanternPicker from '../components/answers/LanternPicker';
import type { LanternOption } from '../components/answers/LanternPicker';
import Keypad from '../components/answers/Keypad';
import Balance from '../components/answers/Balance';
import ActionRow from '../components/answers/ActionRow';
import { SvgAsset } from '../components/exercises/SvgAsset';
import Logo from '../components/brand/Logo';
import CharacterIcon from '../components/adventurer/CharacterIcon';
import FlagQuestionButton from '../components/answers/FlagQuestionButton';
import ChapterCompleteFlash from '../components/ChapterCompleteFlash';
import Confetti from '../components/Confetti';
import EmotionalCheckIn from '../components/EmotionalCheckIn';
import WissensCheckOverlay from '../components/WissensCheckOverlay';
import { useTypewriter } from '../hooks/useTypewriter';
import { useTTS } from '../hooks/useTTS';
import { useSounds } from '../hooks/useSounds';

type BalanceSide = 'left' | 'right';
type Variant = 'lanterns' | 'keypad' | 'balance' | 'textinput';

function detectVariant(ex: Exercise): Variant {
  if (ex.input_kind === 'keypad') return 'keypad';
  if (ex.input_kind === 'balance') return 'balance';
  if (ex.input_kind === 'text') return 'textinput';
  return 'lanterns';
}

// The server sends options already shuffled and without the answer key;
// ``correctAnswer`` is known only once the submit response has arrived.
function buildLanternOptions(ex: Exercise, correctAnswer: string | undefined): LanternOption[] {
  return ex.options.map(o => ({
    label: o,
    value: o,
    correct: correctAnswer === undefined ? undefined : o === correctAnswer,
  }));
}

const ARC_LABELS: Record<string, string> = {
  schatzsuche: 'Finn findet einen Schatz',
  drachenhoehle: 'Die Drachenhöhle',
  zaubermarkt: 'Der Zaubermarkt',
  sternenpfad: 'Der Sternenpfad',
  eispalast: 'Der Eispalast',
  flussdelta: 'Das Flussdelta',
};

// ── Sub-components ───────────────────────────────────────────────────

function TopBar({ adventurerId, arc, xp, xpBurst, streak, streakPulseKey, exerciseIdx, totalEx, klasse, childName, onSwitchChild, onParentDashboard, onSettings, onLearnMode, onLogout, ttsEnabled, ttsAvailable, onToggleTTS }: {
  adventurerId: AdventurerId;
  arc: string;
  xp: number;
  xpBurst: { delta: number; key: number } | null;
  streak: number;
  streakPulseKey: number;
  exerciseIdx: number;
  totalEx: number;
  klasse: number;
  childName?: string;
  onSwitchChild?: () => void;
  onParentDashboard?: () => void;
  onSettings?: () => void;
  onLearnMode?: () => void;
  onLogout?: () => void;
  ttsEnabled: boolean;
  ttsAvailable: boolean;
  onToggleTTS: () => void;
}) {
  const adv = ADVENTURERS[adventurerId];
  const chapterTitle = ARC_LABELS[arc] ?? adv.full;
  return (
    <div style={{
      padding: '14px 32px',
      borderBottom: '1px solid rgba(60,50,40,0.12)',
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      alignItems: 'center',
      gap: 28,
      background: 'var(--parchment)',
    }}>
      {/* logo — fox head (character) + wordmark (brand name) side-by-side.
          The head provides face + personality; the wordmark provides the
          legible brand. Vertical rule separates the brand block from the
          page subtitle. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Logo variant="mark" height={40} />
        <Logo variant="wordmark" height={28} />
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 9, fontWeight: 500,
          letterSpacing: '0.14em',
          color: 'var(--ink-muted)',
          textTransform: 'uppercase',
          paddingLeft: 12,
          marginLeft: 4,
          borderLeft: '1px solid rgba(60,50,40,0.18)',
        }}>{arcTitle(arc)} · Klasse {klasse}</div>
      </div>

      {/* chapter + progress dots */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <h1 style={{
          margin: 0,
          fontFamily: 'var(--serif)',
          fontSize: 16, fontStyle: 'italic',
          color: 'var(--ink)', fontWeight: 500,
        }}>{chapterTitle}</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          {Array.from({ length: totalEx }).map((_, i) => {
            const state = i < exerciseIdx ? 'done' : i === exerciseIdx ? 'current' : 'future';
            return (
              <div key={i} style={{
                width: state === 'current' ? 10 : 7,
                height: state === 'current' ? 10 : 7,
                borderRadius: '50%',
                background: state === 'done' ? 'var(--ink)' : state === 'current' ? 'var(--amber)' : 'transparent',
                border: state === 'future' ? '1.5px solid rgba(60,50,40,0.25)' : 'none',
                transition: 'all 0.25s',
                boxShadow: state === 'current' ? '0 0 0 3px rgba(201,106,47,0.18)' : 'none',
              }} />
            );
          })}
        </div>
      </div>

      {/* XP + parent link */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        {streak >= 2 && (
          <div
            key={`streak-${streakPulseKey}`}
            aria-label={`${streak} richtige in Folge`}
            title={`${streak} richtige in Folge`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px',
              background: 'linear-gradient(135deg, #ffcc80, #ff8a65)',
              borderRadius: 999,
              fontFamily: 'var(--display)', fontSize: 14, fontWeight: 700,
              color: '#7a2e10',
              boxShadow: '0 2px 6px rgba(201,106,47,0.25)',
              animation: 'streak-pulse 0.5s ease-out',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>🔥</span>
            <span>{streak}</span>
          </div>
        )}
        <div style={{ textAlign: 'right', position: 'relative' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.16em', color: 'var(--ink-muted)', textTransform: 'uppercase' }}>XP</div>
          <div
            key={xpBurst?.key ?? 'xp-idle'}
            style={{
              fontFamily: 'var(--display)', fontSize: 20, fontWeight: 600,
              color: 'var(--ink)', lineHeight: 1, marginTop: 2,
              animation: xpBurst ? 'xp-pulse 0.6s ease-out' : undefined,
            }}
          >{xp}</div>
          {xpBurst && xpBurst.delta > 0 && (
            <div
              key={`burst-${xpBurst.key}`}
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: 0, top: -4,
                fontFamily: 'var(--display)', fontSize: 18, fontWeight: 700,
                color: 'var(--amber-deep, #c96a2f)',
                pointerEvents: 'none',
                animation: 'xp-rise 1.1s ease-out forwards',
                textShadow: '0 2px 6px rgba(0,0,0,0.15)',
                whiteSpace: 'nowrap',
              }}
            >+{xpBurst.delta} XP</div>
          )}
        </div>
        {onLearnMode && (
          <button
            onClick={onLearnMode}
            title={`Frag ${adv.name} — stelle ${adv.name} eine Frage`}
            aria-label={`Frag ${adv.name} öffnen`}
            style={{
              minHeight: 44,
              background: 'var(--amber-soft)',
              border: '1.5px solid var(--amber)',
              borderRadius: 3, padding: '10px 14px',
              fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--amber-deep)', cursor: 'pointer',
              fontWeight: 600, whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <CharacterIcon id={adventurerId} size={18} />
            <span>Frag {adv.name}</span>
          </button>
        )}
        {ttsAvailable && (
          <button
            onClick={onToggleTTS}
            title={ttsEnabled ? 'Vorlesen ausschalten' : 'Vorlesen einschalten'}
            aria-label={ttsEnabled ? 'Vorlesen ausschalten' : 'Vorlesen einschalten'}
            aria-pressed={ttsEnabled}
            style={{
              minWidth: 44, minHeight: 44,
              background: ttsEnabled ? 'var(--amber-soft)' : 'none',
              border: `1.5px solid ${ttsEnabled ? 'var(--amber)' : 'rgba(60,50,40,0.2)'}`,
              borderRadius: 3, padding: '6px 10px',
              fontSize: 18, color: ttsEnabled ? 'var(--amber-deep)' : 'var(--ink-muted)',
              cursor: 'pointer', lineHeight: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >{ttsEnabled ? '🔊' : '🔈'}</button>
        )}
        {onSwitchChild && (
          <button
            onClick={onSwitchChild}
            title="Anderes Kind wählen"
            aria-label={`Kind wechseln (jetzt: ${childName ?? ''})`}
            style={{
              minHeight: 44,
              background: 'none', border: '1.5px solid rgba(60,50,40,0.2)',
              borderRadius: 3, padding: '10px 14px',
              fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-muted)', cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 14 }}>👤</span>
            <span>{childName ? `${childName} · wechseln` : 'Kind wechseln'}</span>
          </button>
        )}
        {onSettings && (
          <button
            onClick={onSettings}
            title="Einstellungen"
            style={{
              minHeight: 44,
              background: 'none', border: '1.5px solid rgba(60,50,40,0.2)',
              borderRadius: 3, padding: '10px 14px',
              fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-muted)', cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            aria-label="Einstellungen öffnen"
          >
            <span aria-hidden="true" style={{ fontSize: 14 }}>⚙</span>
            <span>Einstellungen</span>
          </button>
        )}
        {onParentDashboard && (
          <button
            onClick={onParentDashboard}
            title="Elternbereich"
            aria-label="Elternbereich öffnen"
            style={{
              minHeight: 44,
              background: 'none', border: '1.5px solid rgba(60,50,40,0.2)',
              borderRadius: 3, padding: '10px 14px',
              fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-muted)', cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >Elternbereich →</button>
        )}
        {onLogout && (
          <button
            onClick={onLogout}
            title="Abmelden — die Geschichte wird für beim nächsten Mal gespeichert"
            aria-label="Abmelden"
            style={{
              minHeight: 44, minWidth: 44,
              background: 'none', border: '1.5px solid rgba(60,50,40,0.2)',
              borderRadius: 3, padding: '10px 12px',
              fontSize: 16, color: 'var(--ink-muted)',
              cursor: 'pointer', lineHeight: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span aria-hidden="true">⎋</span>
          </button>
        )}
      </div>
    </div>
  );
}

function StoryPanel({
  narrativeShown, narrativeDone, skipNarrative, question, topic, exIdx,
  assetType, assetRef, onListen, listenAvailable,
}: {
  narrativeShown: string;
  narrativeDone: boolean;
  skipNarrative: () => void;
  question: string;
  topic: string;
  exIdx: number;
  assetType?: string;
  assetRef?: string;
  onListen?: (text: string) => void;
  listenAvailable?: boolean;
}) {
  return (
    <div key={exIdx} style={{ animation: 'fade-in-up 0.4s ease-out' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: 'var(--mono)',
        fontSize: 10, letterSpacing: '0.2em',
        color: 'var(--ink-muted)',
        textTransform: 'uppercase',
        marginBottom: 12,
      }}>
        <div style={{ width: 14, height: 1, background: 'var(--amber)' }} />
        Aufgabe {exIdx + 1} · {topic}
      </div>

      <div
        onClick={skipNarrative}
        style={{
          fontFamily: 'var(--serif)',
          fontSize: 20, lineHeight: 1.55,
          color: 'var(--ink)',
          marginBottom: 22,
          minHeight: 120,
          cursor: narrativeDone ? 'default' : 'pointer',
          position: 'relative',
        }}
      >
        <span style={{
          float: 'left',
          fontFamily: 'var(--display)',
          fontSize: 64, lineHeight: 0.85,
          fontWeight: 600,
          color: 'var(--amber-deep)',
          marginRight: 8, marginTop: 4, marginBottom: -4,
        }}>{narrativeShown.charAt(0)}</span>
        <span>{narrativeShown.slice(1)}</span>
        {!narrativeDone && <span style={{
          display: 'inline-block',
          width: 8, height: 20, marginLeft: 2,
          background: 'var(--ink)', opacity: 0.6,
          verticalAlign: 'text-bottom',
          animation: 'cursor-blink 1s steps(2) infinite',
        }} />}
      </div>

      {narrativeDone && assetType === 'svg' && assetRef && (
        <div style={{
          marginBottom: 20,
          padding: 16,
          background: 'var(--card)',
          border: '1px solid rgba(60,50,40,0.12)',
          borderRadius: 4,
          display: 'flex', justifyContent: 'center',
          animation: 'fade-in-up 0.35s ease-out',
        }}>
          <SvgAsset svg={assetRef} className="exercise-svg" ariaLabel={`Bild zur Aufgabe: ${topic}`} />
        </div>
      )}

      {/* Hör-Aufgaben (asset_type='tts'): the passage is spoken, not shown.
          Falls back to rendering the passage as text when speech synthesis
          is unavailable on this device. */}
      {narrativeDone && assetType === 'tts' && assetRef && (
        <div style={{
          marginBottom: 20,
          padding: 16,
          background: 'var(--card)',
          border: '1px solid rgba(60,50,40,0.12)',
          borderRadius: 4,
          animation: 'fade-in-up 0.35s ease-out',
        }}>
          {listenAvailable && onListen ? (
            <button
              onClick={() => onListen(assetRef)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', justifyContent: 'center',
                padding: '14px 18px', cursor: 'pointer',
                fontFamily: 'var(--display)', fontSize: 20, fontWeight: 700,
                color: 'var(--amber-deep)',
                background: 'var(--amber-soft)',
                border: '2px solid var(--amber)', borderRadius: 6,
              }}
            >
              🔊 Anhören
            </button>
          ) : (
            <div style={{ fontFamily: 'var(--display)', fontSize: 18, lineHeight: 1.5, color: 'var(--ink)' }}>
              {assetRef}
            </div>
          )}
        </div>
      )}

      {/* Scene PNG assets (asset_type='image') are rendered on the LEFT
          panel instead, overlapping the arc backdrop — they're atmospheric,
          not diagnostic, so they don't belong crowding the question column.
          SVG assets above stay inline because they ARE the diagram. */}

      {narrativeDone && (
        <div style={{
          fontFamily: 'var(--sans)',
          fontSize: 22, fontWeight: 700,
          color: 'var(--ink)', lineHeight: 1.3,
          marginBottom: 24,
          paddingLeft: 20,
          position: 'relative',
          animation: 'fade-in-up 0.35s ease-out',
        }}>
          <span style={{
            position: 'absolute', left: 0, top: 2,
            fontFamily: 'var(--display)',
            fontSize: 26, color: 'var(--amber)', fontWeight: 600,
          }}>?</span>
          {question}
        </div>
      )}
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────

interface Props {
  adventurerId?: AdventurerId;
  arc?: string;
  profile?: StudentProfile | null;
  onSessionComplete?: () => void;
  onTriggerExplain?: (topicKey: string) => void;
  onSessionExpired?: () => void;
  onParentDashboard?: () => void;
  onSettings?: () => void;
  onLearnMode?: () => void;
  /** Shown when the family has more than one child: opens the child picker. */
  onSwitchChild?: () => void;
  /** Parent-gated sign-out — surfaces an Abmelden button in the top bar.
   *  Backend session state persists across logout, so resuming later just
   *  drops the kid back into the same page. */
  onLogout?: () => void;
}

type ScreenState = 'loading' | 'ready' | 'error';

// "Später" on a Wissens-Check hides it for the rest of this browser session;
// it comes back at the next visit. Storage can be unavailable (private mode).
const CHECK_LATER_KEY = 'finnly-check-later';

function checkPostponed(c: WissensCheck, childId: string): boolean {
  try { return sessionStorage.getItem(CHECK_LATER_KEY) === `${childId}:${c.check_id}:${c.phase}`; } catch { return false; }
}

function postponeCheck(c: WissensCheck, childId: string): void {
  try { sessionStorage.setItem(CHECK_LATER_KEY, `${childId}:${c.check_id}:${c.phase}`); } catch { /* ignore */ }
}

export default function StoryScreen({ adventurerId = 'finn', arc = 'schatzsuche', profile, onSessionComplete, onTriggerExplain, onSessionExpired, onParentDashboard, onSettings, onLearnMode, onSwitchChild, onLogout }: Props) {
  const adv = ADVENTURERS[adventurerId];

  const [screenState, setScreenState] = useState<ScreenState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<PagePayload | null>(null);
  const [exIdx, setExIdx] = useState(0);
  const [, setExerciseCount] = useState(0);
  const [liveXp, setLiveXp] = useState<number | null>(null);
  const [xpBurst, setXpBurst] = useState<{ delta: number; key: number } | null>(null);
  // Consecutive-correct streak — resets on wrong, ticks up on right. Shown as
  // a small 🔥 badge at streak >= 2; the surface flashes briefly on every tick.
  const [streak, setStreak] = useState(0);
  const [streakPulseKey, setStreakPulseKey] = useState(0);

  // answer state
  const [selected, setSelected] = useState<string | null>(null);
  const [balanceSide, setBalanceSide] = useState<BalanceSide | null>(null);
  const [keypadValue, setKeypadValue] = useState('');
  const [textInputValue, setTextInputValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResponse | null>(null);
  const [chapterFlash, setChapterFlash] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [needsMoodCheckIn, setNeedsMoodCheckIn] = useState(false);
  // Open Wissens-Check for the child's class (A5); shown before the mood check-in.
  const [check, setCheck] = useState<WissensCheck | null>(null);

  const [narrative, setNarrative] = useState('');

  const { shown: narrativeShown, done: narrativeDone, skip: skipNarrativeRaw } = useTypewriter(narrative, 18);
  // Wrap skip so we can fire ``narrative_skip_clicked`` telemetry — signals
  // that the narrative is too long for this child, or they're burning through.
  const skipNarrative = useCallback(() => {
    if (!narrativeDone) {
      reportClientEvent('narrative_skip_clicked', {
        exercise_idx: exIdx,
        narrative_len: narrative.length,
        shown_len: narrativeShown.length,
      });
    }
    skipNarrativeRaw();
  }, [skipNarrativeRaw, narrativeDone, exIdx, narrative.length, narrativeShown.length]);

  const { enabled: ttsEnabled, available: ttsAvailable, toggle: toggleTTS, speak: ttsSpeak, speakAlways: ttsSpeakAlways, stop: ttsStop } = useTTS();
  const { playClick, playCorrect, playIncorrect, playChapterComplete } = useSounds();

  // Wrapped setters so answer components don't need to know about sounds —
  // every user-driven selection plays a gentle click; reset effects stay silent.
  const selectLantern = useCallback((v: string) => { playClick(); setSelected(v); }, [playClick]);
  const selectBalance = useCallback((v: 'left' | 'right') => { playClick(); setBalanceSide(v); }, [playClick]);
  const selectKeypad = useCallback((fn: (prev: string) => string) => { playClick(); setKeypadValue(fn); }, [playClick]);

  const ex: Exercise | null = page?.exercises[exIdx] ?? null;
  const isPlaceholder = ex?.placeholder ?? false;
  // Fire ``page_error_shown`` exactly once per placeholder exercise instance —
  // ties the kid's friction moment to a specific broken generation.
  useEffect(() => {
    if (isPlaceholder && ex) {
      reportClientEvent('page_error_shown', {
        exercise_id: ex.exercise_id,
        topic: ex.topic,
      });
    }
  }, [isPlaceholder, ex?.exercise_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const variant: Variant = ex ? detectVariant(ex) : 'lanterns';
  const revealedAnswer = submitResult?.correct_answer || undefined;
  const lanternOptions = useMemo(() => ex ? buildLanternOptions(ex, revealedAnswer) : [], [ex, revealedAnswer]);

  // Hint timeline — fully manual, driven by the 💡 Tipp button in ActionRow.
  const hintCount = ex?.hints?.length ?? 0;
  const { hintTier, advance: advanceHint, canAdvance: hasMoreHints } = useCalloutTimeline({
    hintCount,
    exerciseKey: ex?.exercise_id ?? '',
  });

  const requestHint = useCallback(() => {
    if (!ex) return;
    const nextTier = hintTier + 2; // tier is 1-indexed for telemetry; hintTier starts at -1
    advanceHint();
    void logHintRequested(ex.exercise_id, ex.topic, nextTier).catch(() => {});
  }, [ex, hintTier, advanceHint]);

  // Wait-time lockout: Tipp button is inert for 6s after a new exercise renders,
  // so kids reach for thinking-time before tapping for help.
  const [hintUnlocked, setHintUnlocked] = useState(false);
  // Per-exercise timer. Reset whenever a new question loads; read on submit
  // to populate `time_taken` in the pilot_events row. Tracks "wall-clock from
  // question display to submit" — close enough for analytics, no need to
  // discount typewriter / TTS time.
  const exerciseStartedAtRef = useRef<number>(Date.now());
  useEffect(() => {
    setHintUnlocked(false);
    if (!ex) return;
    exerciseStartedAtRef.current = Date.now();
    const t = window.setTimeout(() => setHintUnlocked(true), HINT_WAIT_TIME_MS);
    return () => window.clearTimeout(t);
  }, [ex?.exercise_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-speak narrative + embedded question once the typewriter finishes.
  // We speak once per exercise; stop any in-flight utterance when the exercise changes.
  useEffect(() => {
    if (!ttsEnabled || !ttsAvailable) return;
    if (!narrativeDone || !ex) return;
    const combined = [narrative, ex.question_text].filter(Boolean).join(' ');
    ttsSpeak(combined);
    return () => ttsStop();
  }, [ttsEnabled, ttsAvailable, narrativeDone, ex?.exercise_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-speak Finn's feedback once the answer is submitted.
  useEffect(() => {
    if (!ttsEnabled || !ttsAvailable) return;
    if (!submitResult) return;
    const text = submitResult.feedback || '';
    if (!text) return;
    ttsSpeak(text);
    return () => ttsStop();
  }, [ttsEnabled, ttsAvailable, submitResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guarded so a double tap on "Weiter" cannot request two pages (the server
  // keeps only the last one, and submits for the other would then 404).
  const nextInFlightRef = useRef(false);
  async function loadNextPage() {
    if (nextInFlightRef.current) return;
    nextInFlightRef.current = true;
    setPageLoading(true);
    try {
      const { page: nextPage, session_complete, exercise_count } = await getNextPage();
      setExerciseCount(exercise_count);
      if (session_complete) {
        onSessionComplete?.();
        return;
      }
      setPage(nextPage);
      setExIdx(0);
      setChapterFlash(true);
      playChapterComplete();
      setTimeout(() => setChapterFlash(false), 2200);
    } catch (e) {
      console.error('next page failed', e);
    } finally {
      setPageLoading(false);
      nextInFlightRef.current = false;
    }
  }

  // init: start session (guard against StrictMode double-mount in dev)
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startSession()
      .then(({ page: p, exercise_count, xp_points, resumed, answered_ids }) => {
        setPage(p);
        setExerciseCount(exercise_count);
        // Resume at the first exercise the server has not committed yet. If the
        // whole page is done (e.g. the child left for the explainer after the
        // last answer), move straight on instead of replaying the page.
        const done = new Set(answered_ids ?? []);
        // Placeholders count as done, matching the server's page-complete rule.
        const firstOpen = p.exercises.findIndex(e => !done.has(e.exercise_id) && !e.placeholder);
        setExIdx(firstOpen === -1 ? Math.max(0, p.exercises.length - 1) : firstOpen);
        setLiveXp(xp_points);
        // Fresh sessions (not resumed) prompt for a mood check-in so Finn can tune tone.
        if (!resumed) setNeedsMoodCheckIn(true);
        setScreenState('ready');
        if (resumed && firstOpen === -1 && p.exercises.length > 0) void loadNextPage();
        // A check is optional extra: if loading it fails, the story just starts.
        getCurrentCheck()
          .then(({ check: c }) => setCheck(c && !checkPostponed(c, profile?.student_id ?? '') ? c : null))
          .catch(() => setCheck(null));
      })
      .catch(e => {
        const msg = String(e);
        // Any 401 (including "Session expired.") means re-login, not a dead end.
        if ((e instanceof ApiError && e.status === 401) || msg.includes('Session not found')) {
          onSessionExpired?.();
        } else {
          setError(msg);
          setScreenState('error');
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-shot session start on mount

  // when exercise changes, load per-exercise narrative from act_narratives
  useEffect(() => {
    if (!ex || !page) return;
    setSelected(null);
    setBalanceSide(null);
    setKeypadValue('');
    setTextInputValue('');
    setSubmitted(false);
    setSubmitResult(null);
    setXpBurst(null);
    const acts = page.scene.act_narratives ?? [];
    const text = acts[exIdx] ?? page.scene.narrative_text ?? '';
    setNarrative(text);
  }, [ex?.exercise_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCorrect = useMemo((): boolean | null => {
    if (!submitted || !submitResult) return null;
    return submitResult.correct;
  }, [submitted, submitResult]);

  const canSubmit = variant === 'lanterns'
    ? !!selected
    : variant === 'keypad'
    ? keypadValue.length > 0
    : variant === 'textinput'
    ? textInputValue.trim().length > 0
    : !!balanceSide;

  const handleSubmit = async () => {
    if (!ex || !page) return;
    let answer = '';
    if (variant === 'lanterns') answer = selected ?? '';
    else if (variant === 'keypad') answer = keypadValue;
    else if (variant === 'textinput') answer = textInputValue.trim();
    else answer = balanceSide === 'left' ? (ex.options[0] ?? '') : balanceSide === 'right' ? (ex.options[1] ?? '') : '';

    setSubmitted(true);
    try {
      const elapsedSecs = Math.max(0, (Date.now() - exerciseStartedAtRef.current) / 1000);
      const result = await submitAnswer({
        exercise_id: ex.exercise_id,
        answer,
        time_taken: Math.round(elapsedSecs * 10) / 10,  // 1-decimal precision
      });
      setSubmitResult(result);
      setExerciseCount(result.exercise_count);
      const prevXp = liveXp ?? profile?.story_state.xp_points ?? 0;
      const delta = result.xp_points - prevXp;
      setLiveXp(result.xp_points);
      if (!result.graded) {
        // Reflections are never graded — calm acknowledgement instead of
        // the right/wrong celebration (no confetti burst, no streak pulse).
        playClick();
        if (delta > 0) setXpBurst({ delta, key: Date.now() });
      } else if (result.correct) {
        playCorrect();
        if (delta > 0) setXpBurst({ delta, key: Date.now() });
        setStreak(s => s + 1);
        setStreakPulseKey(k => k + 1);
      } else {
        playIncorrect();
        setStreak(0);
      }
    } catch (e) {
      console.error('submit failed', e);
      setSubmitted(false);
    }
  };

  // Enter-to-submit for tap-card variants (lanterns + balance). Keypad/textinput
  // wire Enter on their own focused controls; we only need a global listener
  // for the two variants whose tiles consume Space (radio) but not Enter.
  useEffect(() => {
    if (variant !== 'lanterns' && variant !== 'balance') return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Enter') return;
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'textarea' || tag === 'input') return;
      if (!canSubmit || !narrativeDone || submitted) return;
      e.preventDefault();
      handleSubmit();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant, canSubmit, narrativeDone, submitted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = async () => {
    if (!page) return;

    // explainer takes priority over page advance
    if (submitResult?.trigger_explain && submitResult.explain_topic && onTriggerExplain) {
      onTriggerExplain(submitResult.explain_topic);
      return;
    }

    const nextIdx = exIdx + 1;
    if (nextIdx < page.exercises.length) {
      setExIdx(nextIdx);
    } else {
      await loadNextPage();
    }
  };


  if (screenState === 'loading') {
    return (
      <div style={{ display: 'flex', height: '100dvh', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--ink-muted)', fontStyle: 'italic' }}>
        {adv.name} bereitet sich vor…
      </div>
    );
  }

  if (screenState === 'error' || !ex || !page) {
    return (
      <div style={{ display: 'flex', height: '100dvh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 24, color: 'var(--wrong)' }}>Verbindungsfehler</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-muted)' }}>{error}</div>
      </div>
    );
  }

  const baseXp = profile?.story_state.xp_points ?? 0;
  const displayXp = liveXp ?? baseXp;

  return (
    <div className="story-root" style={{
      width: '100%', minHeight: '100dvh',
      background: 'var(--parchment)',
      color: 'var(--ink)',
      fontFamily: 'var(--sans)',
      display: 'flex', flexDirection: 'column',
      position: 'relative',
    }}>
      <TopBar
        adventurerId={adventurerId}
        arc={arc}
        xp={displayXp}
        xpBurst={xpBurst}
        streak={streak}
        streakPulseKey={streakPulseKey}
        exerciseIdx={exIdx}
        totalEx={page.exercises.length}
        klasse={profile?.klasse ?? 2}
        childName={profile?.name}
        onSwitchChild={onSwitchChild}
        onParentDashboard={onParentDashboard}
        onSettings={onSettings}
        onLearnMode={onLearnMode}
        onLogout={onLogout}
        ttsEnabled={ttsEnabled}
        ttsAvailable={ttsAvailable}
        onToggleTTS={toggleTTS}
      />

      <div className="story-grid" style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1.05fr 1fr',
        gap: 32,
        padding: '24px 40px 32px',
        minHeight: 0,
      }}>
        {/* left — scene */}
        <div style={{
          position: 'relative',
          borderRadius: 4,
          border: '1px solid rgba(60,50,40,0.12)',
          overflow: 'hidden',
          minHeight: 0,
          boxShadow: '0 4px 24px rgba(30,25,20,0.10), 0 1px 2px rgba(30,25,20,0.08)',
        }}>
          {/* Per-question scene image (PNG/webp) takes over the whole left
              panel when one is available — no point running the procedural
              arc backdrop underneath competing for attention. Falls back to
              SceneIllustration for questions without a scene asset (the
              geometric SVG questions keep their diagrams inline on the
              right, so the left panel rightly shows the arc atmosphere). */}
          {ex?.asset_type === 'image' && ex.asset_ref ? (
            <div
              key={`scene-${exIdx}`}
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--parchment)',
                animation: 'fade-in-up 0.35s ease-out',
              }}
            >
              <img
                src={ex.asset_ref}
                alt={`Szene zur Aufgabe: ${ex.topic}`}
                loading="lazy"
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </div>
          ) : (
            <SceneIllustration arc={arc} chapterIndex={profile?.story_state.current_chapter_index ?? 0} />
          )}

          {/* hero adventurer + dialogue bubble */}
          <div style={{
            position: 'absolute', left: 24, bottom: 20, right: 24,
            display: 'flex', alignItems: 'flex-end', gap: 16,
            pointerEvents: 'none',
          }}>
            <div style={{
              flexShrink: 0,
              filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.35))',
              pointerEvents: 'auto',
            }}>
              <AdventurerIllustration
                id={adventurerId}
                beat={
                  chapterFlash ? 'celebrating'
                  : !submitted ? 'render'
                  : isCorrect === true ? 'correct'
                  : isCorrect === false ? 'incorrect'
                  : 'idle'
                }
                size={150}
              />
            </div>
            <div style={{ flex: 1, pointerEvents: 'auto', maxWidth: 320 }}>
              {ex && page && (
                <DialogueBubble
                  dialogue={page.scene.character_dialogue || `„${adv.voice}"`}
                  hints={ex.hints || []}
                  correctFeedback={submitResult?.feedback || ''}
                  incorrectFeedback={submitResult?.feedback || ''}
                  beat={!submitted ? 'render' : isCorrect === true ? 'correct' : isCorrect === false ? 'incorrect' : 'idle'}
                  hintTier={hintTier}
                  whisperLabel={`${adv.name} flüstert`}
                />
              )}
            </div>
          </div>

          {chapterFlash && (
            <>
              <ChapterCompleteFlash arcId={arc} />
              <Confetti seedKey={exIdx + 1} />
            </>
          )}

          {pageLoading && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(248,242,226,0.82)',
              backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 12,
              borderRadius: 4,
            }}>
              <div style={{
                fontFamily: 'var(--display)',
                fontSize: 28, fontWeight: 600,
                color: 'var(--ink)', letterSpacing: '-0.01em',
              }}>{adv.name} bereitet die nächste Seite vor…</div>
              <div style={{
                fontFamily: 'var(--mono)', fontSize: 10,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'var(--ink-muted)',
                animation: 'cursor-blink 1s steps(2) infinite',
              }}>Einen Moment</div>
            </div>
          )}
        </div>

        {/* right — story + question + answer */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
          <StoryPanel
            narrativeShown={narrativeShown}
            narrativeDone={narrativeDone}
            skipNarrative={skipNarrative}
            question={ex.question_text}
            topic={ex.topic}
            exIdx={exIdx}
            assetType={ex.asset_type}
            assetRef={ex.asset_ref}
            onListen={ttsSpeakAlways}
            listenAvailable={ttsAvailable}
          />

          <div style={{ marginTop: 'auto' }}>
            {isPlaceholder && (
              <div style={{
                marginBottom: 16,
                padding: '18px 22px',
                background: 'var(--card)',
                border: '1px dashed rgba(60,50,40,0.22)',
                borderRadius: 6,
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
                  color: 'var(--amber)', textTransform: 'uppercase',
                }}>
                  🦊 Kleiner Hoppala
                </div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: 'var(--ink)', lineHeight: 1.5 }}>
                  Oh, diese Aufgabe ist stecken geblieben. Macht nichts — wir probieren die nächste!
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleNext}
                    style={{
                      padding: '12px 24px',
                      background: 'var(--ink)',
                      color: 'var(--parchment)',
                      border: 'none', borderRadius: 3,
                      fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 700,
                      letterSpacing: '0.02em', cursor: 'pointer',
                    }}
                  >Nächste Aufgabe →</button>
                </div>
              </div>
            )}
            {!isPlaceholder && variant === 'lanterns' && (
              <LanternPicker
                options={lanternOptions}
                selected={selected}
                setSelected={selectLantern}
                submitted={submitted}
                disabled={!narrativeDone}
              />
            )}
            {!isPlaceholder && variant === 'keypad' && (
              <Keypad
                value={keypadValue}
                setValue={selectKeypad}
                submitted={submitted}
                isCorrect={isCorrect}
                disabled={!narrativeDone}
                answer={submitResult?.correct_answer ?? ''}
                onEnter={handleSubmit}
                canSubmit={canSubmit && narrativeDone}
              />
            )}
            {!isPlaceholder && variant === 'balance' && (
              <Balance
                left={ex.options[0] ?? ''}
                right={ex.options[1] ?? ''}
                correct={!submitResult?.correct_answer ? null
                  : submitResult.correct_answer === ex.options[0] ? 'left' : 'right'}
                selected={balanceSide}
                setSelected={selectBalance}
                submitted={submitted}
                disabled={!narrativeDone}
              />
            )}
            {!isPlaceholder && variant === 'textinput' && (
              <div style={{ padding: '12px 0 8px' }}>
                {ex.content_type === 'reflection_prompt' && (
                  <div style={{
                    fontFamily: 'var(--display)', fontSize: 15, fontWeight: 600,
                    color: 'oklch(55% 0.06 260)', marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    💭 Zeit zum Nachdenken — es gibt kein Richtig oder Falsch
                  </div>
                )}
                <input
                  type="text"
                  value={textInputValue}
                  onChange={e => { if (!submitted) setTextInputValue(e.target.value); }}
                  onKeyDown={e => { if (e.key === 'Enter' && canSubmit && narrativeDone && !submitted) handleSubmit(); }}
                  disabled={submitted || !narrativeDone}
                  placeholder={ex.content_type === 'reflection_prompt' ? 'Deine Gedanken…' : 'Deine Antwort…'}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '18px 20px',
                    fontFamily: 'var(--display)',
                    fontSize: 26,
                    fontWeight: 600,
                    color: (submitted && isCorrect !== null && ex.content_type !== 'reflection_prompt')
                      ? isCorrect ? 'oklch(40% 0.14 145)' : 'var(--wrong)'
                      : 'var(--ink)',
                    background: (submitted && isCorrect !== null && ex.content_type !== 'reflection_prompt')
                      ? isCorrect ? 'oklch(97% 0.04 145)' : 'oklch(98% 0.04 20)'
                      : 'var(--card)',
                    border: `2px solid ${(submitted && isCorrect !== null && ex.content_type !== 'reflection_prompt')
                      ? isCorrect ? 'oklch(70% 0.18 145)' : 'var(--wrong)'
                      : textInputValue ? 'var(--amber)' : 'rgba(60,50,40,0.18)'}`,
                    borderRadius: 4,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.15s, background 0.2s',
                    letterSpacing: '0.02em',
                  }}
                />
              </div>
            )}

            {!isPlaceholder && <ActionRow
              canSubmit={canSubmit && narrativeDone}
              submitted={submitted}
              isCorrect={isCorrect}
              onSubmit={handleSubmit}
              onNext={handleNext}
              explanation={submitResult?.feedback ?? ''}
              triggerExplain={submitResult?.trigger_explain ?? false}
              hintsTotal={hintCount}
              hintTier={hintTier}
              onRequestHint={requestHint}
              canRequestHint={narrativeDone && hintUnlocked && hasMoreHints}
            />}

            {ex?.exercise_id && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <FlagQuestionButton exerciseId={ex.exercise_id} />
              </div>
            )}
          </div>
        </div>
      </div>

      {check && (
        <WissensCheckOverlay
          check={check}
          childName={profile?.name ?? ''}
          onDone={xp => {
            if (!xp) postponeCheck(check, profile?.student_id ?? '');
            setCheck(null);
            if (xp) setLiveXp(v => (v ?? 0) + xp);
          }}
        />
      )}

      {!check && needsMoodCheckIn && (
        <EmotionalCheckIn
          childName={profile?.name ?? ''}
          onDone={() => setNeedsMoodCheckIn(false)}
        />
      )}
    </div>
  );
}
